import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { retryVideoClock } from '../api';
import { useLiveActivity } from '../hooks/useLiveActivity';
import { useEnv } from '../contexts/EnvContext';

const when = (d) => (d ? dayjs(d).format('MMM D, h:mm A') : '—');
export const ENVIRONMENT_NAMES = {
  production: 'PRODUCTION (bidsy.com)',
  development: 'DEVELOPMENT (dev.bidsy.app)',
};

/**
 * Video clocks that are stuck or overdue (admin panel plan RPT-10), from
 * Bidsy's route 25 on the live poll, with "Run again" on a stuck one
 * (RPT-11). Empty while nothing is stuck. With `connectionId`, only that
 * booking's clocks.
 */
// The environment a request goes to is read from storage at send time (api.js), so a switch in another tab
// would send this one's write elsewhere than its confirm says.
function storedEnv() {
  try {
    return localStorage.getItem('bidsy-env') || 'production';
  } catch {
    return null;
  }
}

export default function StuckClocks({ connectionId = null, onRan = null }) {
  const { data, error, refresh } = useLiveActivity();
  const { env } = useEnv();
  const [confirming, setConfirming] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const section = data?.videoClocks;
  if (!section && !error) return null;
  if (error || section.ok === false) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Stuck video clocks are unavailable: {section?.error || error}. This is a failed read, not zero stuck clocks.
      </Alert>
    );
  }
  if (section.notDeployed) {
    return connectionId == null
      ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Video clocks: not on this environment yet.</Typography>
      : null;
  }

  const clocks = (section.clocks || [])
    .filter((clock) => connectionId == null || Number(clock.connection_id) === Number(connectionId));
  if (!clocks.length && !result) return null;

  const runAgain = async () => {
    const clock = confirming;
    if (storedEnv() !== env) {
      setConfirming(null);
      setResult({ severity: 'error', text: 'The environment was switched in another tab. Reload this page, then run the clock again.' });
      return;
    }
    setRunning(true);
    try {
      const answer = await retryVideoClock(clock.id);
      setResult({ severity: 'success', text: answer?.message || 'The clock is waiting again.' });
    } catch (e) {
      setResult({
        // A 409 is Bidsy's answer ("not stuck any more"), not a fault.
        severity: e?.response?.status === 409 ? 'info' : 'error',
        text: e?.response?.data?.message || e?.message || 'The clock could not be run again.',
      });
    } finally {
      setRunning(false);
      setConfirming(null);
      refresh();
      if (onRan) onRan();
    }
  };

  const total = connectionId == null ? section.counts.stuck + section.counts.overdue : clocks.length;

  return (
    <Paper sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'error.main' }} data-testid="stuck-clocks">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Video clocks stuck or overdue: {total}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A clock run again sticks again if its cause is not fixed. Pass the last error to a developer.
      </Typography>
      {result && <Alert severity={result.severity} onClose={() => setResult(null)} sx={{ mb: 1 }}>{result.text}</Alert>}
      {clocks.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="stuck video clocks">
            <TableHead>
              <TableRow>
                <TableCell>Booking</TableCell>
                <TableCell>What it does</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="right">Tries</TableCell>
                <TableCell>Last error</TableCell>
                <TableCell align="right">Tokens waiting</TableCell>
                <TableCell>Run again by</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {clocks.map((clock) => (
                <TableRow key={clock.id}>
                  <TableCell>
                    <Link to={`/video-bookings/${clock.connection_id}`}>Video call #{clock.connection_id}</Link>
                    {clock.fan && clock.creator ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {clock.fan.username} with {clock.creator.username}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {clock.does || clock.kind}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{clock.kind} · due {when(clock.due_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={clock.state === 'stuck' ? 'error' : 'warning'} label={clock.state === 'stuck' ? 'Stuck' : 'Overdue'} />
                  </TableCell>
                  <TableCell align="right">{clock.attempts}</TableCell>
                  <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>{clock.last_error || '—'}</TableCell>
                  <TableCell align="right">{Number(clock.tokens_held || 0).toLocaleString()}</TableCell>
                  <TableCell>{clock.retried_by ? `${clock.retried_by}, ${when(clock.retried_at)}` : '—'}</TableCell>
                  <TableCell align="right">
                    {clock.state === 'stuck' ? (
                      <Button size="small" variant="outlined" color="error" onClick={() => setConfirming(clock)}>Run again</Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Dialog open={Boolean(confirming)} onClose={() => !running && setConfirming(null)}>
        <DialogTitle>Run this clock again on {ENVIRONMENT_NAMES[env] || env}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Video call #{confirming?.connection_id}: {confirming?.does || confirming?.kind}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            It runs within a minute. It moves no tokens itself: the clock&apos;s own step runs, and does nothing if the booking has moved on.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={running}>Cancel</Button>
          <Button variant="contained" color="error" onClick={runAgain} disabled={running}>
            Run again on {env === 'production' ? 'PROD' : 'DEV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
